<template>
  <export-pdf :loading="loading" filename="faturamento_incremental" title="Análise Faturamento incremental" :subtitle="subtitle">
    <mf-pdf-item v-for="(item, index) in getAllMonthMetrics" :key="item.date" title="Faturamento incremental" :name="$getLoggedUser().client">
      <div class="incremental-revenue">
        <div class="body-chart">
          <incremental-revenue-chart
            :initial-page="index"
            :analysed-period="analysedPeriod"
            :analysed-period-weekly="analysedPeriodWeekly"
            :all-month-metrics="getAllMonthMetrics"
            :is-isolated="isIsolated"
            is-pdf
          />
        </div>
        <div class="body-metrics">
          <incremental-revenue-chart-metrics :formatted-date-label="currentPageFormattedLabel(index)" />
        </div>
      </div>
    </mf-pdf-item>
    <mf-pdf-item :title="`Detalhamento do faturamento incremental - ${subtitle} `" :name="$getLoggedUser().client">
      <dre-incremental-revenue-table />
    </mf-pdf-item>
    <mf-pdf-item title="Cupons identificados x não identificados" :name="$getLoggedUser().client">
      <div class="club-offers-charts">
        <club-card variant="positive" is-pdf />
        <club-card variant="negative" is-pdf />
      </div>
    </mf-pdf-item>
    <mf-pdf-item
      title="Melhores produtos em oferta"
      description="5 melhores produtos em ofertas do clube no mês selecionado, baseado no faturamento incremental."
      :name="$getLoggedUser().client"
    >
      <dre-products-table variant="offer-products" :products="dreProductsMetrics.bestProductsOnOffers" />
    </mf-pdf-item>
    <mf-pdf-item
      title="Piores produtos em oferta"
      description="5 piores produtos em ofertas do clube no mês selecionado, baseado no faturamento incremental."
      :name="$getLoggedUser().client"
    >
      <dre-products-table variant="offer-products" :products="dreProductsMetrics.worstProductsOnOffers" />
    </mf-pdf-item>
    <mf-pdf-item
      title="Melhores produtos fora de oferta do clube"
      description="5 melhores produtos em ofertas do clube no mês selecionado, baseado no aumento das vendas."
      :name="$getLoggedUser().client"
    >
      <dre-products-table variant="out-club-products" :products="dreProductsMetrics.bestProductsOutOffers" />
    </mf-pdf-item>
    <mf-pdf-item
      title="Distribuição dos consumidores"
      description="Distribuição dos consumidores presentes na análise antes do período analisado."
      :name="$getLoggedUser().client"
    >
      <div class="chart-page-consumers">
        <consumers-distribution-card :analysed-period-id="periodId" :analysed-period="period" :analysis-initial-date="analysisInitialDate" is-pdf />
      </div>
    </mf-pdf-item>
    <mf-pdf-item
      title="Distribuição de canais"
      description="Em quais canais os consumidores receberam disparo de mensagens no período analisado."
      :name="$getLoggedUser().client"
    >
      <div class="chart-page-channels">
        <channels-distribution-card :period-id="periodId" :period="period" is-pdf />
      </div>
    </mf-pdf-item>
  </export-pdf>
</template>

<script>
import ExportPdf from '@/components/ExportPdf.vue'
import IncrementalRevenueChart from '../incremental-revenue/IncrementalRevenueChart.vue'
import IncrementalRevenueChartMetrics from '../incremental-revenue/IncrementalRevenueChartMetrics.vue'
import ConsumersDistributionCard from '../consumers-distribution/ConsumersDistributionCard.vue'
import ChannelsDistributionCard from '../consumers-distribution/ChannelsDistributionCard.vue'
import ClubCard from '../club-offers/ClubCard.vue'
import DreProductsTable from './DreProductsTable.vue'
import DreIncrementalRevenueTable from './DreIncrementalRevenueTable.vue'
import { mapGetters } from 'vuex'
import { QUERY_GET_ACCOUNT_IMAGES } from '@/modules/configuracoes-admin/graphql/index.js'
import { DateTime } from 'luxon'

export default {
  name: 'DrePdf',
  components: {
    ExportPdf,
    IncrementalRevenueChart,
    IncrementalRevenueChartMetrics,
    ConsumersDistributionCard,
    ChannelsDistributionCard,
    ClubCard,
    DreProductsTable,
    DreIncrementalRevenueTable,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    subtitle: {
      type: String,
      default: '',
    },
    analysedPeriod: {
      type: String,
      default: '',
    },
    periodId: {
      type: String,
      default: '',
    },
    period: {
      type: Array,
      default: () => [],
    },
    analysisInitialDate: {
      type: String,
      default: '',
    },
    analysedPeriodWeekly: {
      type: Array,
      default: () => ['', ''],
    },
    formattedDateLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters(['dreProductsMetrics', 'analyticsConfiguration', 'dreRevenueIncrementMetrics']),
    logoImage() {
      return this.hotsite?.images?.finish?.logo
    },
    isIsolated() {
      return this.analyticsConfiguration?.model === 'NOT_RECURRENT'
    },
    getAllMonthMetrics() {
      const metrics = this.dreRevenueIncrementMetrics?.periodMetrics?.dailyMetrics
      return Object.values(
        metrics.reduce((acc, item) => {
          const month = item.date.slice(0, 7)
          if (!acc[month]) {
            acc[month] = []
          }
          acc[month].push(item)
          return acc
        }, {})
      )
    },
  },
  apollo: {
    hotsite: {
      query: QUERY_GET_ACCOUNT_IMAGES,
      fetchPolicy: 'network-only',
      update({ account }) {
        return account?.channels?.hotsite?.configuration?.assets
      },
    },
  },
  methods: {
    currentPageFormattedLabel(index) {
      if (!this.isIsolated) return this.formattedDateLabel
      const metric = this.getAllMonthMetrics[index][0]
      if (metric) {
        const formattedDate = DateTime.fromISO(metric.date).setLocale('pt-BR').toFormat("LLLL 'de' yyyy")
        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)
      }
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .full-width {
  width: 100%;
}
.club-offers-charts {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 100px;
}
.incremental-revenue {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: left;
  gap: 10px;
  padding: 20px;
  .body-chart {
    width: 75% !important;
  }
  .body-metrics {
    margin-top: 20px;
  }
}
::v-deep .chart-page-channels {
  width: 80%;
}
::v-deep .chart-page-consumers {
  width: 55%;
  margin-left: 130px;
}
</style>
